// 📄 arrangementService.ts
// Sub-fase K7 (refactor de Configuraciones Cloud/On-Premise): Communication
// Arrangements por SOLUCIÓN (no por canal ABAP) — decisión cerrada en Fase 0
// de K5/K7: 1 subcolección por solución (slpArrangements/sourcingArrangements/
// contractsArrangements/commerceArrangements), cada Arrangement referencia
// un `comunicacionSystemId` del pool (K4). Sin `entornos` DEV/QAS/PRD propios
// — decisión de Fase 0 de K7: los datos reales migrados en K6 fueron
// idénticos entre ambientes, la variación por ambiente ya la carga el
// Communication System referenciado.
import {
  collection, doc, getDocs, setDoc, updateDoc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

export type SolucionConArrangements = "slp" | "sourcing" | "contracts" | "commerce";

export interface ArrangementData {
  nombre: string; // etiqueta libre, ej. "Integración BP"
  comunicacionSystemId: string; // referencia a projects/{id}/comunicacionSystems/{systemId}
  scenarioId: string; // ej. SAP_COM_0008
  arrangementId: string; // ej. INTEGRACION_BP
  descripcion: string;
}

export interface Arrangement extends ArrangementData {
  id: string;
  creadoPor: string;
  creadoEn: any;
  actualizadoEn: any;
}

const coleccion = (projectId: string, solucion: SolucionConArrangements) =>
  collection(db, "projects", projectId, `${solucion}Arrangements`);
const documento = (projectId: string, solucion: SolucionConArrangements, id: string) =>
  doc(db, "projects", projectId, `${solucion}Arrangements`, id);

export const emptyArrangement = (): ArrangementData => ({
  nombre: "", comunicacionSystemId: "", scenarioId: "", arrangementId: "", descripcion: ""
});

export const getArrangements = async (
  projectId: string, solucion: SolucionConArrangements
): Promise<Arrangement[]> => {
  const snap = await getDocs(coleccion(projectId, solucion));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Arrangement, "id">) }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
};

const validar = (data: ArrangementData) => {
  if (!data.nombre.trim()) throw new Error("El nombre es obligatorio.");
  if (!data.comunicacionSystemId) throw new Error("El Communication System asociado es obligatorio.");
  if (!data.scenarioId.trim()) throw new Error("El Scenario ID es obligatorio.");
};

export const crearArrangement = async (
  projectId: string, solucion: SolucionConArrangements, data: ArrangementData, creadoPor: string
): Promise<string> => {
  validar(data);
  const ref = doc(coleccion(projectId, solucion));
  await setDoc(ref, {
    ...data,
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    creadoPor,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarArrangement = async (
  projectId: string, solucion: SolucionConArrangements, id: string, data: ArrangementData
): Promise<void> => {
  validar(data);
  await updateDoc(documento(projectId, solucion, id), {
    ...data,
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarArrangement = async (
  projectId: string, solucion: SolucionConArrangements, id: string
): Promise<void> => {
  await deleteDoc(documento(projectId, solucion, id));
};
